import type { JobPartsStatus, JobStatus } from './api';

export interface DraftLine {
  skuUid: string;
  skuCode: string;
  skuName: string;
  /** A decimal string: some consumables are litres (CLAUDE.md §7.4). */
  qty: string;
}

/** Positive, at most three decimal places — what the API's DECIMAL(14,3) takes. */
export function isValidQty(value: string): boolean {
  if (!/^\d+(\.\d{1,3})?$/.test(value)) return false;
  return Number(value) > 0;
}

const toThousandths = (qty: string): number => {
  const [whole = '0', fraction = ''] = qty.split('.');
  return Number(whole) * 1000 + Number(fraction.padEnd(3, '0'));
};

const fromThousandths = (n: number): string => {
  const whole = Math.floor(n / 1000);
  const fraction = String(n % 1000)
    .padStart(3, '0')
    .replace(/0+$/, '');
  return fraction.length === 0 ? String(whole) : `${String(whole)}.${fraction}`;
};

/**
 * Tapping a part that is already on the request adds one more rather than
 * listing it twice. Done in thousandths so 0.1 + 1 never becomes 1.1000000001.
 */
export function addDraftLine(
  lines: readonly DraftLine[],
  part: Pick<DraftLine, 'skuUid' | 'skuCode' | 'skuName'>,
): DraftLine[] {
  const existing = lines.find((l) => l.skuUid === part.skuUid);
  if (existing === undefined) return [...lines, { ...part, qty: '1' }];
  return lines.map((l) =>
    l.skuUid === part.skuUid
      ? { ...l, qty: isValidQty(l.qty) ? fromThousandths(toThousandths(l.qty) + 1000) : '1' }
      : l,
  );
}

/** Adds decimal-string quantities exactly; blank input is an empty sum of "0". */
export function sumQty(values: readonly string[]): string {
  return fromThousandths(values.reduce((sum, v) => sum + toThousandths(v), 0));
}

const PAST_PARTS: readonly JobStatus[] = [
  'READY_FOR_BILLING',
  'READY_FOR_COLLECTION',
  'COMPLETED',
  'CLOSED',
];

/**
 * Parts are asked for while the work is open. From billing onwards the invoice
 * is about to be (or has been) generated from the job's lines, so a late part
 * would be one nobody bills.
 */
export function canRequestParts(status: JobStatus): boolean {
  return !PAST_PARTS.includes(status);
}

/** A request that still has something the warehouse has not handed over. */
export function hasPartsWaiting(status: JobPartsStatus): boolean {
  return status !== 'ISSUED' && status !== 'CANCELLED';
}
